import React, {
  useState, useEffect, useCallback, useRef,
} from 'react'
import { createPortal } from 'react-dom'
import { ChatInput } from './ChatInput'

export interface MobileComposeOverlayProps {
  isOpen: boolean
  onClose: () => void
  onSendMessage: (message: string) => void
  disabled?: boolean
  isStreaming?: boolean
  placeholder?: string
  /** Controlled text value — shared with the tap target for draft persistence */
  value?: string
  /** Called on every keystroke so the parent tracks the draft */
  onValueChange?: (text: string) => void
  /** Disclaimer text to show below input */
  disclaimer?: string
}

export const MobileComposeOverlay: React.FC<MobileComposeOverlayProps> = ({
  isOpen,
  onClose,
  onSendMessage,
  disabled,
  isStreaming,
  placeholder,
  value,
  onValueChange,
  disclaimer,
}) => {
  const [barBottom, setBarBottom] = useState(0)
  const savedScrollYRef = useRef(0)

  // Reset barBottom when the overlay closes so the next open starts from 0
  // and the CSS transition animates the slide-up again.
  useEffect(() => {
    if (!isOpen) {
      setBarBottom(0)
    }
  }, [isOpen])

  // Save the page scroll position when the overlay opens and restore it
  // when it closes. On mobile browsers, dismissing the keyboard after the
  // overlay unmounts can cause the body to scroll away from the current
  // position (the focus/blur cycle triggers scroll-to-focused-element
  // behavior on the portaled textarea). Restoring the saved position
  // counteracts this.
  useEffect(() => {
    if (!isOpen) return undefined
    savedScrollYRef.current = window.scrollY
    return () => {
      const saved = savedScrollYRef.current
      // Use rAF so the restoration runs after React unmounts the overlay
      // and the browser begins the keyboard dismiss animation.
      requestAnimationFrame(() => {
        if (window.scrollY !== saved) {
          window.scrollTo(0, saved)
        }
      })
    }
  }, [isOpen])

  // Position the input bar above the virtual keyboard using visualViewport.
  // On mobile browsers, `position: fixed; bottom: 0` on a portaled element
  // does NOT get pushed above the keyboard — it gets covered.
  const updatePosition = useCallback(() => {
    const vv = window.visualViewport
    if (!vv) return
    const keyboardHeight = window.innerHeight - (vv.height + vv.offsetTop)
    setBarBottom(Math.max(0, keyboardHeight))
  }, [])

  useEffect(() => {
    if (!isOpen) return undefined
    const vv = window.visualViewport
    if (!vv) return undefined

    updatePosition()
    vv.addEventListener('resize', updatePosition)
    vv.addEventListener('scroll', updatePosition)
    return () => {
      vv.removeEventListener('resize', updatePosition)
      vv.removeEventListener('scroll', updatePosition)
    }
  }, [isOpen, updatePosition])

  if (!isOpen) return null

  const handleSend = (message: string) => {
    onSendMessage(message)
    onClose()
  }

  // Portal to document.body so the overlay lives outside the AppDrawer.
  // This prevents the keyboard from triggering useKeyboardAdjust.
  return createPortal(
    <>
      {/* Backdrop — tap to dismiss */}
      {/* eslint-disable-next-line max-len */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        className="oracle-mobile-compose-overlay__backdrop"
        onClick={onClose}
      />
      {/* Input bar — positioned above keyboard via visualViewport */}
      <div
        className="oracle-mobile-compose-overlay__bar"
        style={{ paddingBottom: `${barBottom + 8}px` }}
      >
        <ChatInput
          onSendMessage={handleSend}
          disabled={disabled}
          isStreaming={isStreaming}
          placeholder={placeholder}
          value={value}
          onValueChange={onValueChange}
          autoFocus
        />
        {disclaimer && (
          <span className="oracle-mobile-compose-overlay__disclaimer">
            {disclaimer}
          </span>
        )}
      </div>
    </>,
    document.body,
  )
}
